<template>
    <div ref="turnstileContainer"></div>
</template>

<script setup lang="ts">
import { ref, onMounted } from "vue";
import { usePage } from "@inertiajs/vue3";

const page = usePage();
const turnstileContainer = ref<HTMLElement | null>(null);
const turnstileWidgetId = ref<string | null>(null);

interface TurnstileEmits {
    (event: "token", token: string): void;
}

const emit = defineEmits<TurnstileEmits>();

interface TurnstileProps {
    theme?: "auto" | "light" | "dark";
    size?: "normal" | "flexible" | "compact";
    appearance?: "always" | "execute" | "interaction-only";
    execution?: "render" | "execute";
}

const props = withDefaults(defineProps<TurnstileProps>(), {
    theme: "auto",
    size: "normal",
    appearance: "execute",
    execution: "execute",
});

// Declare window.turnstile type
declare global {
    interface Window {
        turnstile?: {
            render: (
                container: HTMLElement,
                options: {
                    sitekey: string;
                    theme: string;
                    size: string;
                    appearance: string;
                    execution: string;
                    callback: (token: string) => void;
                },
            ) => string;
            getResponse: (widgetId: string) => string;
            reset: (widgetId: string) => void;
            execute: (widgetId: string) => void;
        };
    }
}

// Load Turnstile script
onMounted(() => {
    if (!document.querySelector('script[src*="turnstile"]')) {
        const script = document.createElement("script");
        script.src =
            "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
        script.defer = true;
        script.onload = () => {
            renderTurnstile();
        };
        document.head.appendChild(script);
    } else if (window.turnstile) {
        renderTurnstile();
    }
});

function renderTurnstile() {
    if (turnstileContainer.value && window.turnstile) {
        const siteKey = page.props.turnstileSiteKey as string;

        if (!siteKey) {
            console.error("Turnstile site key is not configured");
            return;
        }

        turnstileWidgetId.value = window.turnstile.render(
            turnstileContainer.value,
            {
                sitekey: siteKey,
                theme: props.theme,
                size: props.size,
                appearance: props.appearance,
                execution: props.execution,
                callback: (token: string) => {
                    emit("token", token);
                },
            },
        );
    }
}

function getResponse(): string {
    if (turnstileWidgetId.value && window.turnstile) {
        return window.turnstile.getResponse(turnstileWidgetId.value);
    }
    return "";
}

function reset() {
    if (turnstileWidgetId.value && window.turnstile) {
        window.turnstile.reset(turnstileWidgetId.value);
    }
}

function execute() {
    if (turnstileWidgetId.value && window.turnstile) {
        window.turnstile.execute(turnstileWidgetId.value);
    }
}

// Expose methods to parent component
defineExpose({
    getResponse,
    reset,
    execute,
});
</script>
